import { Button, ButtonGroup } from '@components'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronRight, FaTimes } from 'react-icons/fa'
import { Link, useHistory } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { WHEN_NARROW } from '../../layout'

interface NeedsYouRowProps {
  link: string
  tint: string
  color: string
  icon: React.ReactNode
  logo?: string
  title: string
  meta: string
  badge?: React.ReactNode
  onDismiss?: () => void
}

export const NeedsYouRow: React.FC<NeedsYouRowProps> = ({
  link,
  tint,
  color,
  icon,
  logo,
  title,
  meta,
  badge,
  onDismiss
}) => {
  const [t] = useTranslation()
  const history = useHistory()
  const dismissLabel = t('dashboard.needsYou.dismiss')

  const openRow = () => {
    if (isExternalLink(link)) {
      window.open(link, '_blank', 'noopener,noreferrer')
      return
    }
    history.push(link)
  }

  return (
    <Row>
      <RowHit link={link} label={title} />
      <RowIcon $tint={tint} $color={color}>
        {logo ? <img src={logo} alt="" /> : icon}
      </RowIcon>
      <RowText>
        {badge}
        <RowTitle>{title}</RowTitle>
        <RowMeta>{meta}</RowMeta>
      </RowText>
      <ActionGroup>
        {onDismiss && (
          <Button
            type="button"
            size="sm"
            isSecondary
            icon={<FaTimes />}
            aria-label={dismissLabel}
            title={dismissLabel}
            onClick={event => {
              event.preventDefault()
              event.stopPropagation()
              onDismiss()
            }}
          />
        )}
        <Button
          type="button"
          size="sm"
          isSecondary
          icon={<FaChevronRight />}
          aria-label={title}
          title={title}
          onClick={event => {
            event.preventDefault()
            event.stopPropagation()
            openRow()
          }}
        />
      </ActionGroup>
    </Row>
  )
}

export const PartnerPill = styled.span`
  align-self: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing.xxs}rem;
  padding: 0.125rem ${({ theme }) => theme.spacing.sm}rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.neutralGray};
  color: ${({ theme }) => theme.dashboard.textSubtle};
  font-size: ${({ theme }) => theme.fontSize.px10}rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
`

const Row = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.md}rem;
  box-sizing: border-box;
  min-height: 4.5rem;
  padding: ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.lg}rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderSoft};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.defaultHover};
  }

  ${WHEN_NARROW} {
    min-height: 0;
    padding: ${({ theme }) => theme.spacing.md}rem;
  }
`

const rowHitStyles = css`
  position: absolute;
  inset: 0;
  z-index: 0;
  cursor: pointer;
  text-decoration: none;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }
`

const RowHitLink = styled(Link)`
  ${rowHitStyles}
`

const RowHitAnchor = styled.a`
  ${rowHitStyles}
`

/** Backend alerts can send an absolute URL; react-router Link would treat it as in-app. */
const isExternalLink = (link: string) => /^(https?:)?\/\//i.test(link) || link.startsWith('mailto:')

const RowHit: React.FC<{ link: string; label: string }> = ({ link, label }) => {
  if (isExternalLink(link)) {
    return <RowHitAnchor href={link} target="_blank" rel="noopener noreferrer" aria-label={label} />
  }
  return <RowHitLink to={link} aria-label={label} />
}

const RowIcon = styled.div<{ $tint: string; $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  width: 2.125rem;
  height: 2.125rem;
  border-radius: 0.6875rem;
  background: ${({ $tint }) => $tint};
  color: ${({ $color }) => $color};
  font-size: ${({ theme }) => theme.fontSize.px14}rem;
  pointer-events: none;

  img {
    width: 1.25rem;
    height: 1.25rem;
    object-fit: contain;
  }
`

const RowText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxxs}rem;
  flex: 1;
  align-self: stretch;
  min-width: 0;
  pointer-events: none;
`

const RowTitle = styled.span`
  min-width: 0;
  font-size: ${({ theme }) => theme.fontSize.px14}rem;
  font-weight: 600;
  line-height: 1.3;
  color: ${({ theme }) => theme.colors.menuBg};
`

const RowMeta = styled.span`
  min-width: 0;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.iconGray};
`

const ActionGroup = styled(ButtonGroup)`
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  align-self: center;
`
